import { useEffect, useState } from "react";
import i18n from "@/i18n";
import { supabase } from "@/lib/supabase";
import type { FaqItem } from "@/pages/help/faqData";

/** 后台可维护的 FAQ 内容覆盖（按 q_key 存中英文问答）。空字段表示沿用内置默认文案。 */
export interface FaqContentOverride {
  q_key: string;
  category: string | null;
  question_zh: string | null;
  answer_zh: string | null;
  question_en: string | null;
  answer_en: string | null;
  /** 'published' | 'draft'：是否存在未发布的草稿。 */
  status: string | null;
  draft_question_zh: string | null;
  draft_answer_zh: string | null;
  draft_question_en: string | null;
  draft_answer_en: string | null;
  version: number | null;
  published_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** 后台编辑表单提交的内容（发布或存草稿都用它）。 */
export interface FaqContentPayload {
  questionZh: string;
  answerZh: string;
  questionEn: string;
  answerEn: string;
}

/** 一条内容版本快照（用于版本历史与回滚）。 */
export interface FaqVersionRow {
  id: number;
  q_key: string;
  question_zh: string | null;
  answer_zh: string | null;
  question_en: string | null;
  answer_en: string | null;
  action: string;
  changed_fields: string[] | null;
  editor: string | null;
  created_at: string;
}

/** 版本快照里承载的四个内容字段。 */
export interface FaqVersionContent {
  question_zh: string | null;
  answer_zh: string | null;
  question_en: string | null;
  answer_en: string | null;
}

export const FAQ_CONTENT_FIELDS = ["question_zh", "answer_zh", "question_en", "answer_en"] as const;
export type FaqContentField = (typeof FAQ_CONTENT_FIELDS)[number];

/** 渲染用的 FAQ 视图（已合并后台覆盖与内置默认文案）。 */
export interface FaqContentView {
  qKey: string;
  aKey: string;
  cat: string;
  question: string;
  answer: string;
  edited: boolean;
  /** 是否为「最近更新」：已发布内容在近期内被改过。 */
  recent: boolean;
  /** 最近一次发布 / 更新的时间戳，用于悬浮提示。 */
  recentAt: string | null;
}

/** 「最近更新」标记的有效天数窗口。 */
export const RECENT_FAQ_UPDATE_DAYS = 14;

export type FaqLang = "en" | "zh";

/** 当前界面语言，用于挑选覆盖内容的中英文版本。 */
export function faqLang(): FaqLang {
  return (i18n.language || "zh").toLowerCase().startsWith("en") ? "en" : "zh";
}

/** 是否已经有一份「已发布」的后台内容（任一中英文字段非空）。 */
export function hasLiveOverride(override?: FaqContentOverride | null): boolean {
  if (!override) return false;
  return Boolean(
    override.question_zh?.trim() ||
      override.answer_zh?.trim() ||
      override.question_en?.trim() ||
      override.answer_en?.trim(),
  );
}

/** 是否存在未发布的草稿（状态为 draft 且有草稿内容）。 */
export function hasDraft(override?: FaqContentOverride | null): boolean {
  if (!override) return false;
  if (override.status !== "draft") return false;
  return Boolean(
    override.draft_question_zh?.trim() ||
      override.draft_answer_zh?.trim() ||
      override.draft_question_en?.trim() ||
      override.draft_answer_en?.trim(),
  );
}

/** 取出覆盖记录中「已发布」的那一份内容。 */
export function liveContent(override?: FaqContentOverride | null): FaqVersionContent {
  return {
    question_zh: override?.question_zh ?? null,
    answer_zh: override?.answer_zh ?? null,
    question_en: override?.question_en ?? null,
    answer_en: override?.answer_en ?? null,
  };
}

/** 取出覆盖记录中「草稿」的那一份内容；无草稿时回落到已发布内容。 */
export function draftContent(override?: FaqContentOverride | null): FaqVersionContent {
  if (!hasDraft(override)) return liveContent(override);
  return {
    question_zh: override?.draft_question_zh ?? null,
    answer_zh: override?.draft_answer_zh ?? null,
    question_en: override?.draft_question_en ?? null,
    answer_en: override?.draft_answer_en ?? null,
  };
}

/** 把编辑表单内容整理成一份版本快照。 */
/**
 * 取出覆盖记录中「草稿」内容并整理成编辑表单 payload。
 * 草稿为空字段表示沿用默认文案，因此回落到空串（发布时会被转成 null）。
 */
export function draftToPayload(override?: FaqContentOverride | null): FaqContentPayload {
  const draft = draftContent(override);
  return {
    questionZh: draft.question_zh ?? "",
    answerZh: draft.answer_zh ?? "",
    questionEn: draft.question_en ?? "",
    answerEn: draft.answer_en ?? "",
  };
}

/**
 * 判断一条已发布内容是否属于「最近更新」：取发布 / 更新时间戳，落在窗口内才返回该时间戳。
 * 未发布或超出窗口时返回 null。
 */
export function recentlyUpdatedAt(override?: FaqContentOverride | null): string | null {
  if (!override || !hasLiveOverride(override)) return null;
  const stamp = override.published_at || override.updated_at;
  if (!stamp) return null;
  const ts = new Date(stamp).getTime();
  if (!Number.isFinite(ts)) return null;
  return Date.now() - ts <= RECENT_FAQ_UPDATE_DAYS * 24 * 60 * 60 * 1000 ? stamp : null;
}

/** 把编辑表单内容整理成一份版本快照。 */
export function payloadToContent(payload: FaqContentPayload): FaqVersionContent {
  return {
    question_zh: payload.questionZh.trim() || null,
    answer_zh: payload.answerZh.trim() || null,
    question_en: payload.questionEn.trim() || null,
    answer_en: payload.answerEn.trim() || null,
  };
}

/** 比较两份内容，返回发生变化的字段名列表。 */
export function diffContentFields(
  prev: Partial<FaqVersionContent> | null | undefined,
  next: Partial<FaqVersionContent> | null | undefined,
): FaqContentField[] {
  const a = prev ?? {};
  const b = next ?? {};
  return FAQ_CONTENT_FIELDS.filter((field) => (a[field] ?? "") !== (b[field] ?? ""));
}

/**
 * 合并后台覆盖与内置文案：后台有值则优先，字段留空则回落到 i18n 默认。
 * 只读取「已发布」字段，草稿绝不对外展示。
 */
export function resolveFaqView(
  item: FaqItem,
  override: FaqContentOverride | undefined,
  t: (key: string) => string,
  lang: FaqLang = faqLang(),
): FaqContentView {
  const recentAt = recentlyUpdatedAt(override);
  const base: FaqContentView = {
    qKey: item.qKey,
    aKey: item.aKey,
    cat: item.cat,
    question: t(item.qKey),
    answer: t(item.aKey),
    edited: false,
    recent: false,
    recentAt: null,
  };
  if (!override) return base;
  const q = (lang === "en" ? override.question_en : override.question_zh)?.trim();
  const a = (lang === "en" ? override.answer_en : override.answer_zh)?.trim();
  if (!q && !a) return base;
  return {
    ...base,
    question: q || base.question,
    answer: a || base.answer,
    edited: true,
    recent: Boolean(recentAt),
    recentAt,
  };
}

/**
 * 读取后台维护的 FAQ 内容覆盖（含草稿字段）；失败时静默回落到内置文案，绝不阻塞帮助中心。
 * 公共帮助页只消费已发布字段，草稿不会被渲染。
 */
export function useFaqOverrides(): {
  overrides: Record<string, FaqContentOverride>;
  loading: boolean;
} {
  const [overrides, setOverrides] = useState<Record<string, FaqContentOverride>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase.from("faq_content").select("*").limit(1000);
        if (error) throw error;
        const map: Record<string, FaqContentOverride> = {};
        (data ?? []).forEach((row) => {
          const record = row as FaqContentOverride;
          map[record.q_key] = record;
        });
        if (active) setOverrides(map);
      } catch {
        /* backend unavailable, keep defaults */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return { overrides, loading };
}